import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Edit2, Trash2, Eye, EyeOff, Facebook, Instagram, Youtube, Twitter, Linkedin } from 'lucide-react';

const AdminSocialMedia = () => {
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    platform: 'facebook',
    url: '',
    active: true
  });

  const platformIcons = {
    facebook: { icon: Facebook, label: 'Facebook', color: 'text-blue-600' },
    instagram: { icon: Instagram, label: 'Instagram', color: 'text-pink-600' },
    youtube: { icon: Youtube, label: 'YouTube', color: 'text-red-600' },
    twitter: { icon: Twitter, label: 'Twitter', color: 'text-sky-500' },
    tiktok: { icon: 'TikTok', label: 'TikTok', color: 'text-black' },
    linkedin: { icon: Linkedin, label: 'LinkedIn', color: 'text-blue-700' }
  };

  useEffect(() => {
    fetchSocialLinks();
  }, []);

  const fetchSocialLinks = async () => {
    try {
      const response = await api.get('/social-media');
      setSocialLinks(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Social Media Links');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await api.put(`/social-media/${editingId}`, formData);
        setSuccess('✅ Social Media Link erfolgreich aktualisiert');
      } else {
        await api.post('/social-media', formData);
        setSuccess('✅ Social Media Link erfolgreich erstellt');
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ platform: 'facebook', url: '', active: true });
      await fetchSocialLinks();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Speichern des Social Media Links');
    }
  };

  const handleEdit = (link) => {
    setEditingId(link.id);
    setFormData({
      platform: link.platform,
      url: link.url,
      active: link.active
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Social Media Link wirklich löschen?')) {
      return;
    }

    try {
      await api.delete(`/social-media/${id}`);
      setSuccess('✅ Social Media Link erfolgreich gelöscht');
      await fetchSocialLinks();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Fehler beim Löschen des Social Media Links');
    }
  };

  const toggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/social-media/${id}`, { active: !currentStatus });
      await fetchSocialLinks();
    } catch (err) {
      setError('Fehler beim Aktualisieren des Status');
    }
  };

  const getPlatformIcon = (platform) => {
    const Icon = platformIcons[platform]?.icon;
    return Icon ? <Icon className={`w-6 h-6 ${platformIcons[platform].color}`} /> : null;
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Social Media Verwaltung</h1>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingId(null);
            setFormData({ platform: 'facebook', url: '', active: true });
          }}
          className="flex items-center space-x-2 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Neuer Link</span>
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="mb-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingId ? 'Link Bearbeiten' : 'Neuer Social Media Link'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plattform
              </label>
              <select
                value={formData.platform}
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                required
              >
                {Object.entries(platformIcons).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://facebook.com/yourpage"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                required
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <label className="ml-2 text-sm text-gray-700">
                Aktiv (im Footer anzeigen)
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="flex-1 bg-cyan-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
              >
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setFormData({ platform: 'facebook', url: '', active: true });
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Links List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Laden...</p>
        </div>
      ) : socialLinks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">Keine Social Media Links vorhanden</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plattform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {socialLinks.map((link) => (
                <tr key={link.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      {getPlatformIcon(link.platform)}
                      <span className="font-medium">{platformIcons[link.platform]?.label || link.platform}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 truncate max-w-md block"
                    >
                      {link.url}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActive(link.id, link.active)}
                      className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-semibold ${
                        link.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {link.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      <span>{link.active ? 'Aktiv' : 'Inaktiv'}</span>
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(link)}
                        className="text-blue-600 hover:text-blue-800 p-2"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(link.id)}
                        className="text-red-600 hover:text-red-800 p-2"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminSocialMedia;
